import {
  ApiResult,
  ReservationReview,
  VINReviewsResult,
  VINVerificationStatusResponse,
} from '@/models/dto'
import {
  Company,
  CompanyEldTypesResult,
  CompanyResult,
} from '@/models/dto/Company'
import { BusifyFeatureResponse } from '@/models/dto/IntegrationFeature'
import { Tableview } from '@/models/dto/Tableview'
import { TrackingSourcesResult } from '@/models/dto/Tracking'
import { EldType } from '@/models/EldCredential'
import { TableViewParameters } from '@/models/TableView'
import { bffProxyUrl } from '@/utils/env'
import { AxiosResponse } from 'axios'
import Vue from 'vue'

export default {
  /**
   * Retrieves a table view of company data with the given parameters.
   *
   * @param params - An object containing the request parameters, including sorting, filtering, pagination, and page size.
   * @returns the company data for the table view.
   */
  tableView(
    params: TableViewParameters
  ): Promise<AxiosResponse<Tableview<Company>>> {
    const { filters = null, sorts = null, pageSize = 10, page = 1 } = params
    let query = `page=${page}&pageSize=${pageSize}`
    if (sorts) {
      query = `${query}&${sorts}`
    }
    if (filters) {
      query = `${query}&${filters}`
    }
    query = encodeURI(query)
    const host = bffProxyUrl

    return Vue.prototype.$http.get(`${host}/tables/companies?${query}`)
  },
  /**
   * Retrieves the information for a specific company.
   *
   * @param companyId - The ID of the company to retrieve.
   * @returns A promise that resolves to an AxiosResponse with the company information.
   */
  byId(companyId: number): Promise<AxiosResponse<CompanyResult>> {
    const host = bffProxyUrl
    const url = `${host}/companies/${companyId}`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Updates the information for a specific company.
   *
   * @param companyId - The ID of the company to update.
   * @param company - An object containing the updated information for the company.
   * @returns A promise that resolves to an AxiosResponse with a success message.
   */
  update(companyId: number, company: Company): Promise<AxiosResponse<string>> {
    const host = bffProxyUrl
    const url = `${host}/companies/${companyId}`
    return Vue.prototype.$http.patch(url, company)
  },
  /**
   * @param company - Partial company object containing the details to be updated.
   * @returns A promise resolving to the AxiosResponse from the onboarding request.
   */
  onboard(company: Partial<Company>): Promise<AxiosResponse<string>> {
    const host = bffProxyUrl
    const url = `${host}/companies/onboardCUPOPS`
    return Vue.prototype.$http.patch(url, company)
  },
  /**
   * @param companyId - The ID of the company to upload the branding photo for.
   * @param type - The type of branding photo to upload (e.g. "dark" or "light").
   * @param photo - The photo to upload, as a FormData object.
   * @returns A promise resolving to the AxiosResponse from the upload request.
   */
  uploadBranding(
    companyId: number,
    type: string,
    photo: FormData
  ): Promise<AxiosResponse<boolean>> {
    const host = bffProxyUrl
    const url = `${host}/companies/${companyId}/companyBrandings/${type}`
    return Vue.prototype.$http.put(url, photo)
  },
  /**
   * @param payload - FormData object containing the photo to upload and other relevant information.
   * @param companyId - The ID of the company to upload the fleet photo for.
   * @returns A promise resolving to the AxiosResponse from the upload request.
   */
  uploadFleetPhoto(
    payload: FormData,
    companyId: number
  ): Promise<AxiosResponse<ApiResult>> {
    const host = bffProxyUrl
    const url = `${host}/vehicles/${companyId}/vehiclePhotosCharterUp`
    return Vue.prototype.$http.post(url, payload)
  },
  reviews(
    params: TableViewParameters
  ): Promise<AxiosResponse<Tableview<ReservationReview>>> {
    const { sorts = null, filters = null, pageSize = 10, page = 1 } = params

    let query = `page=${page}&pageSize=${pageSize}`
    if (sorts) {
      query += `&${sorts}`
    }
    if (filters) {
      query += `&${filters}`
    }
    query = encodeURI(query)

    const host = bffProxyUrl
    const url = `${host}/reservationReviews?${query}`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Gets the ELD types for a specific company.
   *
   * @returns A promise that resolves to an AxiosResponse with a success message.
   */
  getEldTypes(): Promise<AxiosResponse<CompanyEldTypesResult>> {
    const host = bffProxyUrl
    const url = `${host}/companies/eldTypes`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Updates the ELD types for a specific company.
   *
   * @param eldTypes - The array of ELD types to set for the company.
   * @returns A promise that resolves to an AxiosResponse with a success message.
   */
  updateEldTypes(eldTypes: Array<EldType>): Promise<AxiosResponse<void>> {
    const host = bffProxyUrl
    const url = `${host}/companies/updateEldTypes`
    return Vue.prototype.$http.post(url, eldTypes)
  },
  /**
   * Gets the tracking sources for a specific company.
   *
   * @param companyId - The ID of the company to get tracking sources for.
   * @returns A promise that resolves to an AxiosResponse with a success message.
   */
  getTrackingSources(
    companyId: number
  ): Promise<AxiosResponse<TrackingSourcesResult>> {
    const host = bffProxyUrl
    const url = `${host}/gpsData/view/company/${companyId}`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Gets the active VIN reviews for a specific company.
   *
   * @param companyId - The ID of the company to get VIN reviews for.
   * @returns A promise that resolves to an AxiosResponse with a success message.
   */
  getVINReviews(): Promise<AxiosResponse<VINReviewsResult>> {
    const host = bffProxyUrl
    const url = `${host}/vehicles/vinReviews/getAllForCompany`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Gets the VIN verification statuses for all the vehicles for a specific company.
   *
   * @param companyId - The ID of the company to get VIN verification statuses for.
   * @returns A promise that resolves to an AxiosResponse with a success message.
   */
  getVINVerificationStatuses(
    companyId: number
  ): Promise<AxiosResponse<VINVerificationStatusResponse>> {
    const host = bffProxyUrl
    const url = `${host}/vehicles/${companyId}/getVINVerificationStatuses`
    return Vue.prototype.$http.get(url)
  },
  /**
   * Gets the Busify integration features for a specific company.
   *
   * @param companyId - The ID of the company to get Busify integration features for.
   * @returns A promise that resolves to an AxiosResponse with the Busify integration features.
   */
  getBusifyIntegrationFeatures(
    companyId: number
  ): Promise<AxiosResponse<BusifyFeatureResponse>> {
    const host = bffProxyUrl
    const url = `${host}/companies/${companyId}/busify/features`
    return Vue.prototype.$http.get(url)
  },
}
